
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Document {
  id: string;
  name: string;
  display_name?: string;
  file_path: string;
  section_id: string;
  created_at: string;
  size: number;
  position: number;
  content_type: string;
  created_by: string | null;
  creator_email: string | null;
  creator_name: string | null;
  organization_id: string;
}

export async function deleteDocument(doc: Document, onSuccess?: (docId: string) => void) {
  try {
    // Delete file from storage
    const { error: storageError } = await supabase.storage
      .from('documents')
      .remove([doc.file_path]);

    if (storageError) throw storageError;

    // Delete document record
    const { error: dbError } = await supabase
      .from('documents')
      .delete()
      .eq('id', doc.id);

    if (dbError) throw dbError;

    toast.success('Document deleted successfully');
    onSuccess?.(doc.id);
  } catch (error: any) {
    console.error('Error deleting document:', error);
    toast.error(error.message || 'Failed to delete document');
  }
}

export async function updateDocumentDisplayName(docId: string, displayName: string | null) {
  try {
    const { error } = await supabase
      .from('documents')
      .update({ display_name: displayName })
      .eq('id', docId);

    if (error) throw error;

    toast.success('Document name updated successfully');
  } catch (error: any) {
    console.error('Error updating document display name:', error);
    toast.error(error.message || 'Failed to update document name');
    throw error;
  }
}

export async function moveDocument(
  doc: Document, 
  documents: Document[], 
  direction: 'up' | 'down', 
  onSuccess?: () => void
) {
  const currentIndex = documents.findIndex(d => d.id === doc.id);
  if ((direction === 'up' && currentIndex === 0) || 
      (direction === 'down' && currentIndex === documents.length - 1)) {
    return;
  }

  const otherDoc = direction === 'up' 
    ? documents[currentIndex - 1] 
    : documents[currentIndex + 1];

  try {
    // Update both documents with all required fields, preserving creator information
    const updates = [
      {
        ...doc,
        position: otherDoc.position,
        creator_email: doc.creator_email,
        creator_name: doc.creator_name
      },
      {
        ...otherDoc,
        position: doc.position,
        creator_email: otherDoc.creator_email,
        creator_name: otherDoc.creator_name
      }
    ];

    const { error } = await supabase
      .from('documents')
      .upsert(updates);

    if (error) throw error;

    // Refresh the documents list
    onSuccess?.();
    toast.success('Document order updated');
  } catch (error: any) {
    console.error('Error reordering documents:', error);
    toast.error(error.message || 'Failed to reorder documents');
  }
}
